import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

function useCountUp(target, duration, inView) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;

    const frames = Math.round(duration / 16);
    let frame = 0;

    const timer = setInterval(() => {
      frame++;
      const progress = frame / frames;
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.floor(eased * target));
      if (frame >= frames) {
        setCount(target);
        clearInterval(timer);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return count;
}

function Stat({ value, prefix = '', suffix = '', label, duration = 1800 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const count = useCountUp(value, duration, inView);

  return (
    <div ref={ref} className="flex flex-col items-center justify-center py-10 px-6 text-center">
      <div className="text-4xl md:text-5xl font-extrabold text-gray-900 tabular-nums">
        {prefix}{count.toLocaleString()}{suffix}
      </div>
      <div className="mt-2 text-sm md:text-base text-gray-500 max-w-[160px] leading-snug">
        {label}
      </div>
    </div>
  );
}

export default function StatsBar() {
  return (
    <section className="bg-white border-y border-gray-100">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100">
          <Stat value={13} suffix=" min" label="less daily EHR time per clinician" />
          <Stat value={40} suffix="%" prefix="~" label="less after-hours documentation" />
          <Stat value={8} label="clinical specialties supported" />
          <Stat value={5} prefix="$" suffix="K+" label="estimated annual OHIP revenue per provider" duration={1400} />
        </div>
        <p className="text-center text-xs text-gray-400 py-3 border-t border-gray-100">
          13 min/day: JAMA multicenter study, Apr 2026 (n=8,581 clinicians across 5 health systems).{' '}
          <a href="/blog/measuring-impact-of-scribes/" className="text-primary-600 hover:underline">
            Read the research →
          </a>
        </p>
      </div>
    </section>
  );
}
